import { useState, useCallback, useMemo, useRef } from 'react';

// 跨分頁複製的剪貼簿識別前綴
const CLIPBOARD_PREFIX = 'WHATFLOW_NODES:';

// 移除節點 data 內無法 JSON 序列化的 function/component 屬性
// icon 是 forwardRef 物件（如 PlayCircleOutlined），JSON.stringify 後會變成 {}，
// 貼上時 React.createElement({}) 會拋 error #130，故必須一併移除。
const sanitizeNodeForClipboard = (node) => {
  const { data, measured, ...rest } = node;
  // eslint-disable-next-line no-unused-vars
  const { onDelete, icon, ...restData } = data || {};
  return { ...rest, data: restData };
};

// 高級功能 Hook - 處理多選、對齊、複製貼上等功能
// nodeTypesList: 從 useWorkflowState 傳入的 { type, icon, iconColor, ... }[] 陣列，
// 用於在跨分頁貼上時還原 icon component（icon 無法 JSON 序列化，貼上時需重新查表）
export const useAdvancedFeatures = (nodes, setNodes, edges, setEdges, selectedNodes, setSelectedNodes, nodeTypesList = []) => {
  // 複製狀態（同分頁 fallback 用）
  const [copiedNodes, setCopiedNodes] = useState([]);
  const [copiedEdges, setCopiedEdges] = useState([]);
  
  // 多選移動狀態
  const [dragStartPosition, setDragStartPosition] = useState(null);
  const [initialNodePositions, setInitialNodePositions] = useState({});
  
  // 複製節點功能：同時寫入系統剪貼簿（支援跨分頁/跨瀏覽器）
  const copyNodes = useCallback(async () => {
    if (selectedNodes.length === 0) return;
    
    const nodesToCopy = nodes.filter(node => selectedNodes.includes(node.id));
    // 一併複製兩端都在選中範圍內的 edges
    const selectedSet = new Set(selectedNodes);
    const edgesToCopy = edges.filter(
      e => selectedSet.has(e.source) && selectedSet.has(e.target)
    );

    // 存入 state 作為 fallback（剪貼簿 API 不可用時使用）
    setCopiedNodes(nodesToCopy);
    setCopiedEdges(edgesToCopy);

    // 序列化後寫入系統剪貼簿
    const payload = {
      version: 1,
      nodes: nodesToCopy.map(sanitizeNodeForClipboard),
      edges: edgesToCopy,
    };
    try {
      await navigator.clipboard.writeText(CLIPBOARD_PREFIX + JSON.stringify(payload));
      console.log('已複製節點到系統剪貼簿:', nodesToCopy.length, '個節點,', edgesToCopy.length, '條連線');
    } catch (e) {
      console.warn('系統剪貼簿 API 不可用，使用內部狀態作為 fallback:', e.message);
    }
  }, [selectedNodes, nodes, edges]);

  // type → icon component 查表（nodeTypesList 傳入後 memoize）
  const iconByType = useMemo(() => {
    const map = {};
    nodeTypesList.forEach(cfg => {
      if (cfg.type && cfg.icon) map[cfg.type] = cfg.icon;
    });
    return map;
  }, [nodeTypesList]);

  // 從解析出的原始節點陣列建立帶偏移的新節點，並回傳 oldId → newId 的對應表
  const buildPastedNodes = useCallback((rawNodes) => {
    const idMap = {};
    const now = Date.now();
    const newNodes = rawNodes.map((node, index) => {
      const newId = `${node.id}_copy_${now}_${index}`;
      idMap[node.id] = newId;
      return {
        ...node,
        id: newId,
        position: {
          x: node.position.x + 50 + index * 20,
          y: node.position.y + 50 + index * 20,
        },
        selected: false,
        data: {
          ...node.data,
          // 從 nodeTypesList 還原 icon（JSON 序列化時已被 strip，需重新查表）
          icon: iconByType[node.data?.type] ?? node.data?.icon,
          onDelete: node.data?.type !== 'start'
            ? (id) => setNodes(nds => nds.filter(n => n.id !== id))
            : null,
        },
      };
    });
    return { newNodes, idMap };
  }, [iconByType, setNodes]);

  // 貼上節點功能：優先從系統剪貼簿讀取，不行才用 state fallback
  const pasteNodes = useCallback(async () => {
    let rawNodes = null;
    let rawEdges = [];

    // 嘗試從系統剪貼簿讀取
    try {
      const text = await navigator.clipboard.readText();
      if (text.startsWith(CLIPBOARD_PREFIX)) {
        const payload = JSON.parse(text.slice(CLIPBOARD_PREFIX.length));
        if (payload.version === 1 && Array.isArray(payload.nodes) && payload.nodes.length > 0) {
          rawNodes = payload.nodes;
          rawEdges = payload.edges || [];
          console.log('從系統剪貼簿讀取到節點:', rawNodes.length);
        }
      }
    } catch (e) {
      console.warn('無法讀取系統剪貼簿，改用內部狀態:', e.message);
    }

    // Fallback：使用同分頁複製的 state
    if (!rawNodes) {
      if (copiedNodes.length === 0) return;
      rawNodes = copiedNodes.map(sanitizeNodeForClipboard);
      rawEdges = copiedEdges;
      console.log('使用內部狀態貼上節點:', rawNodes.length);
    }

    if (rawNodes.length === 0) return;

    const { newNodes, idMap } = buildPastedNodes(rawNodes);

    // 重新對應 edge 兩端的節點 id
    const newEdges = rawEdges
      .filter(e => idMap[e.source] && idMap[e.target])
      .map(e => ({
        ...e,
        id: `${e.id}_copy_${Date.now()}`,
        source: idMap[e.source],
        target: idMap[e.target],
      }));

    setNodes(nds => [...nds, ...newNodes]);
    if (newEdges.length > 0) {
      setEdges(eds => [...eds, ...newEdges]);
    }
    setSelectedNodes(newNodes.map(n => n.id));
    console.log('已貼上節點:', newNodes.length, '個,', newEdges.length, '條連線');
  }, [copiedNodes, copiedEdges, buildPastedNodes, setNodes, setEdges, setSelectedNodes]);

  // 取得節點實際渲染尺寸：
  // React Flow v12 將實際量測尺寸放在 node.measured.{width,height}，
  // 舊版才放在 node.width / node.height。
  // 注意：量測未完成時 measured 可能為 { width: 0, height: 0 }，
  // 使用 ?? 會把 0 當成有效值，導致置中／右對齊計算錯誤（形同只對齊左緣）。
  const pickPositiveSize = (...candidates) => {
    for (const v of candidates) {
      if (typeof v === 'number' && Number.isFinite(v) && v > 0) return v;
    }
    return null;
  };
  const getNodeWidth = (node) =>
    pickPositiveSize(node?.measured?.width, node?.width, node?.data?.width) ?? 160;
  const getNodeHeight = (node) =>
    pickPositiveSize(node?.measured?.height, node?.height, node?.data?.height) ?? 60;

  // 對齊節點功能
  const alignNodes = useCallback((alignment) => {
    if (selectedNodes.length < 2) {
      console.log('Not enough selected nodes for alignment:', selectedNodes.length);
      return;
    }

    // 用 functional setNodes 取得最新的 nodes（避免 closure 拿到舊值，
    // 也避免在 React Flow 內部 onNodesChange 之後被覆蓋）。
    setNodes((prevNodes) => {
      const selectedSet = new Set(selectedNodes);
      const nodesToAlign = prevNodes.filter((n) => selectedSet.has(n.id));
      if (nodesToAlign.length < 2) {
        console.log('Not enough nodes found for alignment:', nodesToAlign.length);
        return prevNodes;
      }

      console.log('alignNodes called:', {
        alignment,
        nodes: nodesToAlign.map((n) => ({
          id: n.id,
          position: n.position,
          width: getNodeWidth(n),
          height: getNodeHeight(n),
        })),
      });

      let leftMost, topMost, rightMostEdge, bottomMostEdge, targetCenterX, targetCenterY;
      switch (alignment) {
        case 'align-left':
          leftMost = Math.min(...nodesToAlign.map((n) => n.position.x));
          break;
        case 'align-right':
          rightMostEdge = Math.max(
            ...nodesToAlign.map((n) => n.position.x + getNodeWidth(n))
          );
          break;
        case 'align-center': {
          const centers = nodesToAlign.map((n) => n.position.x + getNodeWidth(n) / 2);
          targetCenterX = (Math.min(...centers) + Math.max(...centers)) / 2;
          break;
        }
        case 'align-top':
          topMost = Math.min(...nodesToAlign.map((n) => n.position.y));
          break;
        case 'align-bottom':
          bottomMostEdge = Math.max(
            ...nodesToAlign.map((n) => n.position.y + getNodeHeight(n))
          );
          break;
        case 'align-middle': {
          const centersY = nodesToAlign.map((n) => n.position.y + getNodeHeight(n) / 2);
          targetCenterY = (Math.min(...centersY) + Math.max(...centersY)) / 2;
          break;
        }
        default:
          return prevNodes;
      }

      const computeNewPosition = (node) => {
        const w = getNodeWidth(node);
        const h = getNodeHeight(node);
        const { x, y } = node.position;
        switch (alignment) {
          case 'align-left':
            return { x: leftMost, y };
          case 'align-right':
            return { x: rightMostEdge - w, y };
          case 'align-center':
            return { x: targetCenterX - w / 2, y };
          case 'align-top':
            return { x, y: topMost };
          case 'align-bottom':
            return { x, y: bottomMostEdge - h };
          case 'align-middle':
            return { x, y: targetCenterY - h / 2 };
          default:
            return { x, y };
        }
      };

      const updated = prevNodes.map((node) => {
        if (!selectedSet.has(node.id)) return node;
        return { ...node, position: computeNewPosition(node) };
      });

      console.log('已對齊', nodesToAlign.length, '個節點:', alignment);
      return updated;
    });
  }, [selectedNodes, setNodes]);


  // 處理鍵盤事件
  const handleKeyDown = useCallback((event) => {
    // 檢查是否在輸入框、文本區域或可編輯元素中
    const target = event.target;
    const isInputElement = target.tagName === 'INPUT' || 
                          target.tagName === 'TEXTAREA' || 
                          target.contentEditable === 'true' ||
                          target.isContentEditable ||
                          // 檢查是否在 Ant Design 的輸入組件中
                          target.closest('.ant-input') ||
                          target.closest('.ant-input-affix-wrapper') ||
                          target.closest('.ant-select-selector') ||
                          target.closest('.ant-cascader-picker') ||
                          target.closest('.ant-picker') ||
                          target.closest('.ant-mentions') ||
                          target.closest('.ant-upload');
    
    // 如果在輸入元素中，不攔截 Ctrl+C 和 Ctrl+V
    if (isInputElement && (event.key === 'c' || event.key === 'v')) {
      return; // 讓瀏覽器處理複製/貼上
    }
    
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'c':
          event.preventDefault();
          copyNodes(); // async，不需 await（結果不影響後續流程）
          break;
        case 'v':
          event.preventDefault();
          pasteNodes(); // async，不需 await
          break;
      }
    }
    
    if (event.key === 'Delete' || event.key === 'Backspace') {
      if (selectedNodes.length > 0) {
        event.preventDefault();
        // 刪除選中的節點（除了 start 節點）
        const nodesToDelete = selectedNodes.filter(id => {
          const node = nodes.find(n => n.id === id);
          return node && node.data.type !== 'start';
        });
        
        if (nodesToDelete.length > 0) {
          // 刪除節點
          setNodes(nds => nds.filter(n => !nodesToDelete.includes(n.id)));
          
          // 同時刪除所有連接到這些節點的 edges
          setEdges(eds => eds.filter(e => !nodesToDelete.includes(e.source) && !nodesToDelete.includes(e.target)));
          
          setSelectedNodes([]);
          console.log('已刪除節點:', nodesToDelete.length);
        }
      }
    }
  }, [selectedNodes, nodes, setNodes, setEdges, copyNodes, pasteNodes]);

  return {
    copyNodes,
    pasteNodes,
    alignNodes,
    handleKeyDown,
  };
};
